import { IconButton, Modal, Text } from "@carbon6/c6-frontend-design";
import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import {
  completeDatasetUpload,
  deleteDatasetUploadStagedFile,
  listDatasetUploadStagedFiles,
  stagedFilesTotalBytes,
  type StagedFile,
} from "@/api";
import { hatcheryTextModes } from "@/app/theme";
import { HatModalHeader } from "@/components/modal";
import { truncationMarkerProps } from "@/components/table/truncationCell";
import { EmptyState } from "@/shared/ui/emptyState/public";
import { LoadingState } from "@/shared/ui/loadingState/public";
import { fetchAllStagedFiles, uploadEngine, type UploadEngine } from "@/upload";
import { formatUploadBytes, resolveErrorMessage } from "../datasetUploadFormatters";
import styles from "./StagedFilesReviewDialog.module.css";

type StagedFilesReviewDialogProps = {
  uploadUuid: string;
  displayName: string;
  onClose: () => void;
  // 완료·삭제로 서버 상태가 바뀐 뒤 목록을 갱신한다(선택 prop).
  onChanged?: () => void | Promise<void>;
  // 테스트 주입용. 기본은 앱 스코프 싱글턴이다.
  engine?: UploadEngine;
};

// 한 화면에 보여줄 staged 행 수(로컬 페이지네이션. probe 는 이미 전체를 수집한다).
const pageSize = 100;

/**
 * staged 파일 검토·명시적 완료 다이얼로그. complete 전(PENDING_UPLOAD) 잡의 staging 실측
 * 목록을 보여준다. 잘못 올라간 파일 삭제와 '완료'(UPLOADED 전이)를 제공한다.
 * 엔진이 같은 잡을 검토 대기(awaitingComplete)로 가지고 있으면 완료를 엔진 경로로 실행한다.
 * 그래서 위젯 상태와 다르지 않다.
 */
export function StagedFilesReviewDialog({
  uploadUuid,
  displayName,
  onClose,
  onChanged,
  engine = uploadEngine,
}: StagedFilesReviewDialogProps): ReactNode {
  const [files, setFiles] = useState<StagedFile[] | null>(null);
  // 목록 조회 실패는 목록 자리가 알린다. 삭제·완료 실패는 목록이 그대로 보이는 동작 오류다.
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [deletingPath, setDeletingPath] = useState<string | null>(null);
  const [page, setPage] = useState(1);

  const load = useCallback(async (): Promise<void> => {
    setLoadError(null);
    try {
      const all = await fetchAllStagedFiles(
        (query) => listDatasetUploadStagedFiles(uploadUuid, query),
        pageSize,
      );
      setFiles(all);
    } catch (error) {
      setLoadError(resolveErrorMessage(error));
    }
  }, [uploadUuid]);

  useEffect(() => {
    void load();
  }, [load]);

  const total = useMemo(() => stagedFilesTotalBytes(files ?? []), [files]);
  const totalPages = Math.max(1, Math.ceil((files?.length ?? 0) / pageSize));
  const currentPage = Math.min(page, totalPages);
  const visible = (files ?? []).slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const handleDelete = async (relativePath: string): Promise<void> => {
    setDeletingPath(relativePath);
    setActionError(null);
    try {
      await deleteDatasetUploadStagedFile(uploadUuid, relativePath);
      setFiles((current) => current?.filter((file) => file.relative_path !== relativePath) ?? null);
      await onChanged?.();
    } catch (error) {
      setActionError(resolveErrorMessage(error));
    } finally {
      setDeletingPath(null);
    }
  };

  const handleComplete = async (): Promise<void> => {
    setBusy(true);
    setActionError(null);
    try {
      // 엔진이 검토 대기로 가진 잡이면 엔진 경로로 완료한다. 그래서 위젯 상태도 함께 바뀐다.
      const engineJob = engine
        .getSnapshot()
        .jobs.find(
          (job) => job.uploadJob?.uuid === uploadUuid && job.status === "awaitingComplete",
        );
      if (engineJob !== undefined) {
        engine.complete(engineJob.id);
      } else {
        await completeDatasetUpload(uploadUuid);
      }
      await onChanged?.();
      onClose();
    } catch (error) {
      setActionError(resolveErrorMessage(error));
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      onClose={busy ? () => undefined : onClose}
      size="lg"
      closeOnBackdropClick={!busy}
      closeOnEscape={!busy}
      aria-labelledby="staged-files-review-title"
    >
      <HatModalHeader
        title="업로드할 파일을 검토합니다."
        titleId="staged-files-review-title"
        close={{ disabled: busy }}
      />
      <div className={styles.body}>
        <Text variant={hatcheryTextModes["caption-normal"]} className={styles.subtitle}>
          {displayName} — 서버 스테이징에 올라간 파일 목록입니다. 완료하면 처리(검증·반영)가
          시작되고 더는 수정할 수 없습니다.
        </Text>

        {files === null ? (
          loadError === null ? (
            <LoadingState>파일 목록을 불러오고 있습니다.</LoadingState>
          ) : (
            <EmptyState tone="error" action={{ label: "다시 시도", onClick: () => void load() }}>
              {loadError}
            </EmptyState>
          )
        ) : files.length === 0 ? (
          <EmptyState>
            스테이징에 올라간 파일이 없습니다. 파일을 업로드한 뒤 완료할 수 있습니다.
          </EmptyState>
        ) : (
          <ul className={styles.fileList} aria-label="스테이징 파일 목록">
            {visible.map((file) => (
              <li key={file.relative_path} className={styles.fileRow}>
                <span className={styles.filePath} {...truncationMarkerProps}>
                  {file.relative_path}
                </span>
                <span className={styles.fileSize}>
                  {file.size_bytes === null ? "-" : formatUploadBytes(file.size_bytes)}
                </span>
                <IconButton
                  text="삭제"
                  size="sm"
                  variant="ghost"
                  tone="danger"
                  disabled={busy || deletingPath !== null}
                  onClick={() => void handleDelete(file.relative_path)}
                />
              </li>
            ))}
          </ul>
        )}

        {totalPages > 1 ? (
          <div className={styles.pager}>
            <IconButton
              text="이전"
              size="sm"
              variant="surface"
              disabled={currentPage <= 1}
              onClick={() => setPage(currentPage - 1)}
            />
            <Text variant={hatcheryTextModes["caption-normal"]}>
              {`${currentPage} / ${totalPages}`}
            </Text>
            <IconButton
              text="다음"
              size="sm"
              variant="surface"
              disabled={currentPage >= totalPages}
              onClick={() => setPage(currentPage + 1)}
            />
          </div>
        ) : null}

        {actionError !== null ? (
          <Text variant={hatcheryTextModes["body-normal"]} className={styles.error} role="alert">
            {actionError}
          </Text>
        ) : null}

        <div className={styles.footer}>
          <Text variant={hatcheryTextModes["caption-normal"]} className={styles.summary}>
            {/* 크기 미상이 섞이면 합계가 실제보다 작다는 사실이 합계만으로는 보이지 않는다.
                그래서 건수를 함께 표시한다. */}
            {files === null ? "" : summaryLabel(files.length, total)}
          </Text>
          <div className={styles.actions}>
            <IconButton
              text="닫기"
              type="button"
              variant="surface"
              disabled={busy}
              onClick={onClose}
            />
            <IconButton
              text={busy ? "완료 중…" : "완료"}
              type="button"
              variant="solid"
              disabled={busy || files === null || files.length === 0}
              onClick={() => void handleComplete()}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
}

// 푸터 한 줄. 합계 뒤에 크기 미상 건수를 추가한다. 미상이 없으면 합계만 표시한다.
function summaryLabel(fileCount: number, total: { bytes: number; unknownCount: number }): string {
  const base = `총 ${fileCount.toLocaleString()}개 · ${formatUploadBytes(total.bytes)}`;
  return total.unknownCount === 0
    ? base
    : `${base} (${total.unknownCount.toLocaleString()}개 크기 미상)`;
}
